/**
 * Represents a function that can be used as an event listener.
 * @callback EventListener
 * @param value - The arguments passed to the listener.
 * @returns The return value of the listener.
 */
export type EventListener = (...value: any[]) => void | Promise<void>;

/**
 * Manages event listeners and event emission.
 */
export class EventManager {
  private explicitListeners = new Map<string, Map<string, EventListener>>();
  private globalListeners = new Map<string, EventListener>();

  /**
   * Emits an event to all registered listeners.
   * @param eventType - The type of event to emit.
   * @param param - The parameter to pass to the listeners.
   */
  public emit(eventType: string, param?: unknown): void {
    console.log('[EventEmitter::emit]', eventType, [...(this.explicitListeners.get(eventType) ?? []), ...this.globalListeners].length, param);
    const listeners = [...this.globalListeners.values(), ...this.explicitListeners.get(eventType)?.values() ?? []];
    for (const listener of listeners) {
      listener(eventType, param);
    }
  }

  /**
   * Adds a global listener that will be called for all event types.
   * @param listener - The listener function to add.
   */
  public addGlobalListener(key: string, listener: EventListener): void {
    this.globalListeners.set(key, listener);
  }

  /**
   * Removes a global listener.
   * @param listener - The listener function to remove.
   */
  public removeGlobalListener(key: string, listener: EventListener): void {
    this.globalListeners.delete(key);
  }

  /**
   * Adds a listener for a specific event type.
   * @param eventType - The type of event to listen for.
   * @param listener - The listener function to add.
   */
  public addListener(eventType: string, listenerKey: string, listener: EventListener): void {
    if (!this.explicitListeners.has(eventType)) {
      this.explicitListeners.set(eventType, new Map());
    }
    this.explicitListeners.get(eventType)?.set(listenerKey, listener);
  }

  /**
   * Removes a listener for a specific event type.
   * @param eventType - The type of event the listener was registered for.
   * @param listener - The listener function to remove.
   * @returns True if the listener was found and removed, false otherwise.
   */
  public removeListener(eventType: string, listenerKey: string): boolean {
    return this.explicitListeners.get(eventType)?.delete(listenerKey) ?? false;
  }

  /**
   * Removes all listeners for a specific event type, or all listeners if no event type is specified.
   * @param eventType - The type of event to remove listeners for. If not provided, all listeners will be removed.
   */
  public removeAllListeners(eventType?: string): void {
    if (eventType) {
      this.explicitListeners.delete(eventType);
    } else {
      this.explicitListeners.clear();
    }
  }
}

/**
 * A default instance of the EventManager.
 */
export default new EventManager();
